import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider }  from '@/context/AuthContext';
import Layout            from '@/components/layout/Layout';
import ProtectedRoute    from '@/components/ProtectedRoute';
import AdminRoute        from '@/components/AdminRoute';

// ── Page-level loading fallback ───────────────────────────────────────────────
function PageSpinner() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <span className="font-mono text-xs text-text-muted animate-pulse">loading...</span>
    </div>
  );
}

// ── Public site pages — lazy loaded ──────────────────────────────────────────
const HomePage            = lazy(() => import('@/pages/HomePage'));
const PostPage            = lazy(() => import('@/pages/PostPage'));
const ArticlePage         = lazy(() => import('@/pages/ArticlePage'));
const CategoryPage        = lazy(() => import('@/pages/CategoryPage'));
const SearchPage          = lazy(() => import('@/pages/SearchPage'));
const UserProfilePage     = lazy(() => import('@/pages/UserProfilePage'));
const AccountSettingsPage = lazy(() => import('@/pages/AccountSettingsPage'));
const LoginPage           = lazy(() => import('@/pages/auth/Login'));
const RegisterPage        = lazy(() => import('@/pages/auth/Register'));
const DashboardPage       = lazy(() => import('@/pages/DashboardPage'));
const NotFoundPage        = lazy(() => import('@/pages/NotFoundPage'));
const AboutPage           = lazy(() => import('@/pages/AboutPage'));
const PrivacyPage         = lazy(() => import('@/pages/PrivacyPage'));
const TermsPage           = lazy(() => import('@/pages/TermsPage'));

// ── Admin pages — separate lazy chunk ────────────────────────────────────────
const AdminLayout     = lazy(() => import('@/layouts/AdminLayout'));
const AdminDashboard  = lazy(() => import('@/pages/admin/Dashboard'));
const AdminPostsPage  = lazy(() => import('@/pages/admin/PostsPage'));
const PostEditor      = lazy(() => import('@/pages/admin/PostEditor'));
const AdminComments   = lazy(() => import('@/pages/admin/CommentsPage'));
const AdminUsers      = lazy(() => import('@/pages/admin/UsersPage'));
const AdminAds        = lazy(() => import('@/pages/admin/AdsPage'));
const AdminNewsletter = lazy(() => import('@/pages/admin/NewsletterPage'));
const AdminAudit      = lazy(() => import('@/pages/admin/AuditPage'));
const AdminSettings   = lazy(() => import('@/pages/admin/SettingsPage'));
const AdminSeeder     = lazy(() => import('@/pages/admin/SeederPage'));
const AdminImport     = lazy(() => import('@/pages/admin/ImportPage'));
const CategoriesPage  = lazy(() => import('@/pages/CategoriesPage'));
const TagsPage        = lazy(() => import('@/pages/TagsPage'));

export default function App() {
  return (
    <AuthProvider>
      <Suspense fallback={<PageSpinner />}>
        <Routes>
          {/* ── Public site — main Layout (header + ticker + footer) ─────── */}
          <Route element={<Layout />}>
            {/* Public */}
            <Route path="/"                          element={<HomePage />} />
            <Route path="/posts/:slug"               element={<PostPage />} />
            {/* Static paths — must come before the /:categorySlug/:postSlug wildcard */}
            <Route path="/category/:slug"            element={<CategoryPage />} />
            <Route path="/search"                    element={<SearchPage />} />
            <Route path="/u/:username"               element={<UserProfilePage />} />
            {/* /:categorySlug/:postSlug — pretty article URLs */}
            <Route path="/:categorySlug/:postSlug"   element={<ArticlePage />} />
            <Route path="/categories"                element={<CategoriesPage />} />
            <Route path="/tags"                      element={<TagsPage />} />
            <Route path="/about"                     element={<AboutPage />} />
            <Route path="/privacy"                   element={<PrivacyPage />} />
            <Route path="/terms"                     element={<TermsPage />} />
            <Route path="/login"                     element={<LoginPage />} />
            <Route path="/register"                  element={<RegisterPage />} />

            {/* Authenticated users */}
            <Route element={<ProtectedRoute />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/account"   element={<AccountSettingsPage />} />
            </Route>
          </Route>

          {/* ── Admin panel — completely separate AdminLayout ────────────── */}
          {/* AdminRoute guards the entire subtree (EDITOR + ADMIN only)     */}
          <Route element={<AdminRoute />}>
            <Route element={<AdminLayout />}>
              <Route path="/admin"                     element={<AdminDashboard />} />
              <Route path="/admin/posts"               element={<AdminPostsPage />} />
              <Route path="/admin/posts/new"           element={<PostEditor />} />
              <Route path="/admin/posts/:id/edit"      element={<PostEditor />} />
              <Route path="/admin/comments"            element={<AdminComments />} />
              <Route path="/admin/users"               element={<AdminUsers />} />
              <Route path="/admin/ads"                 element={<AdminAds />} />
              <Route path="/admin/newsletter"          element={<AdminNewsletter />} />
              <Route path="/admin/audit"               element={<AdminAudit />} />
              <Route path="/admin/settings"            element={<AdminSettings />} />
              <Route path="/admin/seeder"              element={<AdminSeeder />} />
              <Route path="/admin/import"              element={<AdminImport />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}
